import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaBookOpen,
  FaCheck,
  FaCheckCircle,
  FaExchangeAlt,
  FaGraduationCap,
  FaHome,
  FaPlus,
  FaSave,
  FaSyncAlt,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import MyLoader from "../Spinner/MyLoader";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function studentInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

//Created By Cursor
function getStudentPhoto(item) {
  const raw =
    item?.student_img_path ||
    item?.student_image ||
    item?.photo ||
    item?.photo_url ||
    "";
  const path = String(raw).trim();
  if (!path || path === "null" || path === "undefined") return "";
  // API sometimes builds https://host/ with empty path
  if (/^https?:\/\/[^/]+\/?$/i.test(path)) return "";
  return path;
}

//Created By Cursor
function parseApiResponse(response) {
  if (typeof response === "string") {
    try {
      let cleaned = response.trim();
      const jsonStart = cleaned.search(/[{\[]/);
      if (jsonStart > 0) cleaned = cleaned.substring(jsonStart);
      return JSON.parse(cleaned);
    } catch {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          return JSON.parse(jsonMatch[0]);
        } catch {
          return null;
        }
      }
      return null;
    }
  }
  return response;
}

//Created By Cursor
function getStudentId(student) {
  return student?.student_enrollment_no || student?.id;
}

//Created By Cursor
function getSubjectId(subject) {
  return subject?.subject_id || subject?.id;
}

//Created By Cursor
function isActiveStatus(status) {
  return status === "Active" || status === "active";
}

//Created By Cursor
const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="sts-card__avatar-wrap" aria-hidden>
      {showImg ? (
        <img
          src={imagePath}
          alt=""
          className="sts-card__avatar"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="sts-card__avatar sts-card__avatar--initials">
          {studentInitials(name)}
        </span>
      )}
    </div>
  );
});

//Created By Cursor
const StudentCard = memo(function StudentCard({
  item,
  index,
  selectedSubjects,
  busy,
  onToggleAlt,
  onUpdate,
}) {
  const studentId = getStudentId(item) || index;
  const mainSubs = Array.isArray(item.main_subject) ? item.main_subject : [];
  const altSubs = Array.isArray(item.alt_subject) ? item.alt_subject : [];
  const classLabel =
    item.section_name != null && item.section_name !== ""
      ? `${item.class_name} · ${item.section_name}`
      : item.class_name || "—";
  const photo = getStudentPhoto(item);

  const activeAltCount = altSubs.filter((s) => {
    const sid = getSubjectId(s);
    return selectedSubjects[`${studentId}_${sid}`];
  }).length;

  return (
    <article className="sts-card" style={{ "--sts-i": index }}>
      <div className="sts-card__head">
        <div className="sts-card__who">
          <StudentAvatar name={item.stu_name} imagePath={photo} />
          <div className="min-w-0">
            <h3 className="sts-card__name">
              {item.stu_name || "Student"}
              {item.student_enrollment_no ? (
                <span className="sts-card__enroll">
                  {item.student_enrollment_no}
                </span>
              ) : null}
            </h3>
            {item.father_name ? (
              <p className="sts-card__meta-line">{item.father_name}</p>
            ) : null}
            <p className="sts-card__meta-line">
              <FaGraduationCap aria-hidden /> {classLabel}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="sts-btn sts-btn--primary sts-btn--sm"
          disabled={busy || altSubs.length === 0}
          onClick={() => onUpdate(studentId)}
        >
          <FaSave />
          <span>Update</span>
        </button>
      </div>

      <div className="sts-card__grid">
        <section className="sts-panel sts-panel--main">
          <div className="sts-panel__head">
            <h4>
              <FaCheckCircle aria-hidden /> Main subjects
            </h4>
            <span>{mainSubs.length}</span>
          </div>
          <div className="sts-chips">
            {mainSubs.length === 0 ? (
              <span className="sts-muted">No main subjects</span>
            ) : (
              mainSubs.map((sub, i) => (
                <span
                  key={`main-${studentId}-${getSubjectId(sub) || i}`}
                  className="sts-chip sts-chip--main"
                >
                  <FaCheck aria-hidden />
                  {sub.subject_name || getSubjectId(sub)}
                </span>
              ))
            )}
          </div>
        </section>

        <section className="sts-panel sts-panel--alt">
          <div className="sts-panel__head">
            <h4>
              <FaExchangeAlt aria-hidden /> Alternative
            </h4>
            <span>
              {activeAltCount}/{altSubs.length}
            </span>
          </div>
          <div className="sts-chips">
            {altSubs.length === 0 ? (
              <span className="sts-muted">No alternative subjects</span>
            ) : (
              altSubs.map((sub, i) => {
                const subjectId = getSubjectId(sub) || i;
                const key = `${studentId}_${subjectId}`;
                const isSelected = Boolean(selectedSubjects[key]);
                const wasActive = isActiveStatus(sub.status);
                const showSaved = wasActive && isSelected;
                return (
                  <button
                    key={`alt-${studentId}-${subjectId}`}
                    type="button"
                    className={`sts-chip sts-chip--toggle${
                      showSaved
                        ? " is-saved"
                        : isSelected
                        ? " is-on"
                        : " is-off"
                    }`}
                    onClick={() => onToggleAlt(studentId, subjectId)}
                    disabled={busy}
                    title={
                      isSelected
                        ? "Click to deactivate"
                        : "Click to activate"
                    }
                  >
                    {showSaved || isSelected ? (
                      <FaCheckCircle aria-hidden />
                    ) : (
                      <FaPlus aria-hidden />
                    )}
                    {sub.subject_name || subjectId}
                  </button>
                );
              })
            )}
          </div>
        </section>
      </div>
    </article>
  );
});

//Created By Cursor
export default function StudentSubject() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionID, setSessionID] = useState("");
  const [classID, setClassID] = useState("");
  const [className, setClassName] = useState("");
  const [sectionID, setSectionID] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState("0");
  const [mStudentSubject, setStudentSubject] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  //Created By Cursor
  const getClassStudentSubject = useCallback(
    (class_id, section_id, session_id, { soft = false } = {}) => {
      if (!class_id || !session_id) {
        setStudentSubject([]);
        setSelectedSubjects({});
        return;
      }
      if (soft) setRefreshing(true);
      else setLoading(true);
      setStudentSubject([]);
      setSelectedSubjects({});

      const schoolCode = SessionManager.getSchoolCode();
      axios
        .get(`${API_BASE()}getClassStudentSubject.php`, {
          params: {
            school_code: schoolCode,
            session_id,
            class_id,
            section_id,
          },
        })
        .then((res) => res.data)
        .then((response) => {
          if (response.api_status === "OK" && Array.isArray(response.data)) {
            setStudentSubject(response.data);
            const initialSelections = {};
            response.data.forEach((student) => {
              const studentId = getStudentId(student);
              if (student.alt_subject && Array.isArray(student.alt_subject)) {
                student.alt_subject.forEach((altSub) => {
                  const subjectId = getSubjectId(altSub);
                  if (isActiveStatus(altSub.status)) {
                    initialSelections[`${studentId}_${subjectId}`] = true;
                  }
                });
              }
            });
            setSelectedSubjects(initialSelections);
          } else {
            setStudentSubject([]);
            if (response.api_remark) {
              myToast.notify_warning(response.api_remark);
            }
          }
        })
        .catch(() => {
          myToast.notify_error("Failed to load student subjects");
          setStudentSubject([]);
        })
        .finally(() => {
          setLoading(false);
          setRefreshing(false);
        });
    },
    []
  );

  //Created By Cursor
  const callBackClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position,
      class_name,
      section_name
    ) => {
      setClassID(class_id);
      setClassName(class_name || "");
      setSectionID(section_id);
      setSectionName(section_name || "");
      setSessionID(session_id);
      setSelectedClassPosition(selected_position || "0");
      getClassStudentSubject(class_id, section_id, session_id);
    },
    [getClassStudentSubject]
  );

  const handleSubjectToggle = useCallback((studentId, subjectId) => {
    setSelectedSubjects((prev) => {
      const key = `${studentId}_${subjectId}`;
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  //Created By Cursor
  const buildSubjectList = useCallback(
    (student, studentId) => {
      if (!student?.alt_subject || !Array.isArray(student.alt_subject)) {
        return [];
      }
      return student.alt_subject
        .map((altSub) => {
          const subjectId = getSubjectId(altSub);
          if (!subjectId) return null;
          const key = `${studentId}_${subjectId}`;
          return {
            subject_id: subjectId,
            status: selectedSubjects[key] ? "Active" : "Inactive",
          };
        })
        .filter(Boolean);
    },
    [selectedSubjects]
  );

  //Created By Cursor
  const handleApiResponse = useCallback(
    (response, successMessage, errorMessage, onSuccess) => {
      const parsed = parseApiResponse(response);
      const apiStatus =
        parsed?.api_status || parsed?.apiStatus || "";
      if (apiStatus && String(apiStatus).toUpperCase() === "OK") {
        myToast.notify_success(successMessage);
        if (onSuccess) onSuccess();
      } else {
        myToast.notify_error(
          parsed?.api_remark ||
            parsed?.apiRemark ||
            parsed?.message ||
            errorMessage
        );
      }
    },
    []
  );

  //Created By Cursor
  const handleUpdate = useCallback(
    (studentId) => {
      if (!sessionID || !classID || !sectionID) {
        myToast.notify_warning("Please select a class and section first");
        return;
      }

      const student = mStudentSubject.find(
        (item) =>
          String(getStudentId(item)) === String(studentId) ||
          String(item.id) === String(studentId)
      );
      if (!student) {
        myToast.notify_warning("Student not found");
        return;
      }

      const actualStudentId = getStudentId(student) || studentId;
      const validSubjects = buildSubjectList(student, actualStudentId);
      if (validSubjects.length === 0) {
        myToast.notify_warning("No valid subjects found to update");
        return;
      }

      const staffDetails = SessionManager.getStaffDetails();
      if (!staffDetails?.staff_id) {
        myToast.notify_error("Staff information not found. Please login again.");
        return;
      }
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_error("School code not found");
        return;
      }

      setLoading(true);
      const formData = new FormData();
      formData.append("staff_id", staffDetails.staff_id);
      formData.append("session_id", sessionID);
      formData.append("class_id", classID);
      formData.append("section_id", sectionID);
      formData.append("student_id", actualStudentId);
      formData.append("subject_list", JSON.stringify(validSubjects));
      formData.append("school_code", schoolCode);

      axios
        .post(`${API_BASE()}updateStudentAltSubjects.php`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => parseApiResponse(res.data))
        .then((response) => {
          handleApiResponse(
            response,
            "Student subjects updated successfully",
            "Failed to update student subjects",
            () => {
              getClassStudentSubject(classID, sectionID, sessionID, {
                soft: true,
              });
            }
          );
        })
        .catch((error) => {
          myToast.notify_error(
            error.response?.data?.api_remark ||
              "Failed to update student subjects. Please try again."
          );
        })
        .finally(() => setLoading(false));
    },
    [
      sessionID,
      classID,
      sectionID,
      mStudentSubject,
      buildSubjectList,
      handleApiResponse,
      getClassStudentSubject,
    ]
  );

  const studentListForUpdate = useMemo(() => {
    if (!mStudentSubject?.length) return [];
    return mStudentSubject
      .map((student) => {
        const actualStudentId = getStudentId(student);
        if (!actualStudentId) return null;
        const subjectList = buildSubjectList(student, actualStudentId);
        if (subjectList.length === 0) return null;
        return { student_id: actualStudentId, subject_list: subjectList };
      })
      .filter(Boolean);
  }, [mStudentSubject, buildSubjectList]);

  //Created By Cursor
  const handleUpdateAll = useCallback(() => {
    if (!sessionID || !classID || !sectionID) {
      myToast.notify_warning("Please select a class and section first");
      return;
    }
    if (studentListForUpdate.length === 0) {
      myToast.notify_warning("No valid students with subjects found to update");
      return;
    }

    const staffDetails = SessionManager.getStaffDetails();
    if (!staffDetails?.staff_id) {
      myToast.notify_error("Staff information not found. Please login again.");
      return;
    }
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_error("School code not found");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("staff_id", staffDetails.staff_id);
    formData.append("session_id", sessionID);
    formData.append("class_id", classID);
    formData.append("section_id", sectionID);
    formData.append("student_list", JSON.stringify(studentListForUpdate));
    formData.append("school_code", schoolCode);

    axios
      .post(`${API_BASE()}updateAllStudentAltSubjects.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => parseApiResponse(res.data))
      .then((response) => {
        handleApiResponse(
          response,
          `Successfully updated subjects for ${studentListForUpdate.length} student(s)`,
          "Failed to update all students subjects",
          () => {
            getClassStudentSubject(classID, sectionID, sessionID, {
              soft: true,
            });
          }
        );
      })
      .catch((error) => {
        myToast.notify_error(
          error.response?.data?.api_remark ||
            "Failed to update all students subjects. Please try again."
        );
      })
      .finally(() => setLoading(false));
  }, [
    sessionID,
    classID,
    sectionID,
    studentListForUpdate,
    handleApiResponse,
    getClassStudentSubject,
  ]);

  const hasStudents = mStudentSubject.length > 0;
  const busy = loading || refreshing;
  const classLabel = className
    ? `${className}${sectionName ? ` · ${sectionName}` : sectionID ? ` · Sec ${sectionID}` : ""}`
    : "Select a class";

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page sts-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Student Subject</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell sts-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUserGraduate />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Student Subject</h1>
              <p className="sm-hero__sub">
                Assign alternative subjects per student · {classLabel}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`sts-hero-icon${refreshing ? " is-spinning" : ""}`}
              title="Refresh"
              disabled={busy || !classID || !sessionID}
              onClick={() =>
                getClassStudentSubject(classID, sectionID, sessionID, {
                  soft: true,
                })
              }
            >
              <FaSyncAlt />
            </button>
            <button
              type="button"
              className="sts-btn sts-btn--hero"
              disabled={busy || !hasStudents}
              onClick={handleUpdateAll}
            >
              <FaCheckCircle />
              <span>Update all</span>
            </button>
          </div>
        </header>

        <div className="sts-body">
          <div className="sts-class">
            <SelectClass
              action={callBackClassSelect}
              selected_position={selectedClassPosition || "0"}
            />
          </div>

          {hasStudents ? (
            <div className="sts-toolbar">
              <div className="sts-kpi">
                <FaUsers aria-hidden />
                <strong>{mStudentSubject.length}</strong>
                <span>students</span>
              </div>
              <div className="sts-kpi sts-kpi--soft">
                <FaBookOpen aria-hidden />
                <span>Toggle alternatives, then Update</span>
              </div>
              <button
                type="button"
                className="sts-btn sts-btn--primary"
                disabled={busy}
                onClick={handleUpdateAll}
              >
                <FaCheckCircle />
                <span>Update all</span>
              </button>
            </div>
          ) : null}

          {loading && !hasStudents ? (
            <div className="sts-loading">
              <MyLoader />
            </div>
          ) : hasStudents ? (
            <div className="sts-list">
              {mStudentSubject.map((item, index) => (
                <StudentCard
                  key={`${getStudentId(item) || index}-${index}`}
                  item={item}
                  index={index}
                  selectedSubjects={selectedSubjects}
                  busy={busy}
                  onToggleAlt={handleSubjectToggle}
                  onUpdate={handleUpdate}
                />
              ))}
            </div>
          ) : (
            <div className="sts-empty">
              <div className="sts-empty__icon" aria-hidden>
                <FaUsers />
              </div>
              <h3>Select a class</h3>
              <p>
                Choose class and section to view students and manage their
                alternative subjects.
              </p>
            </div>
          )}

          {busy && hasStudents ? (
            <div className="sts-busy-overlay" aria-busy="true">
              <MyLoader />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
